<template>
  <el-dialog
    title="设置车型信息"
    :visible.sync="visible"
    width="480px"
    :close-on-click-modal="false"
    :append-to-body="true"
    @close="handleClose"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-position="top"
    >
      <el-form-item label="车龄" prop="carAge">
        <el-select
          v-model="formData.carAge"
          placeholder="请选择"
          clearable
          style="width: 100%"
        >
          <el-option label="默认设置" :value="-1" />
          <el-option
            v-for="item in carAgeOptions"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="表显里程" prop="mileage">
        <el-select
          v-model="formData.mileage"
          placeholder="请选择"
          clearable
          style="width: 100%"
        >
          <el-option label="默认设置" :value="-1" />
          <el-option
            v-for="item in mileageOptions"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="车证">
        <div style="display: flex; align-items: center">
          <el-radio-group v-model="formData.carCertMode">
            <el-radio label="default">默认设置</el-radio>
            <el-radio label="custom">自定义</el-radio>
          </el-radio-group>
          <el-checkbox
            v-model="formData.hasLicense"
            :style="{ visibility: formData.carCertMode === 'custom' ? 'visible' : 'hidden', marginLeft: '20px' }"
          >
            有车证
          </el-checkbox>
        </div>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">
        保存
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { getSingleCarConfig, setSingleCarConfig } from '@/api/goods'
import { Message } from '@qs/ui'
import { onMounted, reactive, ref } from 'vue'
import { useBatchSession } from '../hooks/useBatchSession'

const emit = defineEmits(['confirm'])

const { sessionId, carAgeOptions, mileageOptions, fetchEnums } =
  useBatchSession()

const visible = ref(false)
const loading = ref(false)
const formRef = ref(null)

// 当前操作的车型
const currentModel = ref(null)

// 表单数据
const formData = reactive({
  carAge: -1,
  mileage: -1,
  carCertMode: 'default', // 'default' | 'custom'
  hasLicense: false,
})

// 校验规则
const formRules = {}

// 关闭弹窗
const handleClose = () => {
  visible.value = false
  formRef.value?.resetFields()
}

// 确认保存
const handleConfirm = async () => {
  // 表单验证
  try {
    await formRef.value?.validate()
  } catch {
    return
  }

  loading.value = true

  // 根据 carCertMode 确定 carCert 的值
  let carCert
  if (formData.carCertMode === 'default') {
    carCert = -1 // 默认设置
  } else {
    carCert = formData.hasLicense ? 1 : 0 // 自定义：1-有车证，0-无车证
  }

  const res = await setSingleCarConfig({
    sessionId: sessionId.value,
    carModelId: currentModel.value?.carModelId,
    age: formData.carAge,
    mileage: formData.mileage,
    carCert,
  })

  loading.value = false

  if (res?.code === 1) {
    Message.success('保存成功')
    emit('confirm', { ...formData, carModelId: currentModel.value?.carModelId })
    handleClose()
  }
  // 失败时 axios 已显示错误提示
}

// 打开弹窗
const open = async (model) => {
  currentModel.value = model
  visible.value = true

  // 调用 API 获取车型配置数据
  const res = await getSingleCarConfig({
    sessionId: sessionId.value,
    carModelId: model?.carModelId,
  })

  if (res?.code === 1 && res.data) {
    const { age, mileage, carCert } = res.data

    // 回填车龄
    formData.carAge = age

    // 回填表显里程
    formData.mileage = mileage

    // 回填车证
    if (carCert === -1) {
      // 默认设置
      formData.carCertMode = 'default'
      formData.hasLicense = false
    } else {
      // 自定义
      formData.carCertMode = 'custom'
      formData.hasLicense = carCert === 1
    }
  }
}

onMounted(() => {
  // 从 Vuex 加载枚举数据（带缓存）
  fetchEnums()
})

defineExpose({
  open,
})
</script>
